<script setup lang="ts">
import { ref, watch } from "vue";
import Icon from "./Icon.vue";
import { api, type BrowseResult } from "../api";

const props = defineProps<{ serverId: string; start: string }>();
const emit = defineEmits<{ pick: [string]; close: [] }>();

const path = ref(props.start || "/");
const data = ref<BrowseResult | null>(null);
const err = ref("");
const busy = ref(false);

async function load(p: string) {
  busy.value = true;
  err.value = "";
  try {
    const r = await api.get<BrowseResult>(
      `/api/servers/${encodeURIComponent(props.serverId)}/browse?path=${encodeURIComponent(p)}`,
    );
    data.value = r;
    // Bierzemy ścieżkę zwróconą przez serwer, nie tę wpisaną: serwer ją
    // normalizuje (rozwija "..", ucina końcowe ukośniki), więc to ona jest
    // prawdą o tym, gdzie faktycznie jesteśmy.
    path.value = r.path;
  } catch (e) {
    err.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

watch(() => props.serverId, () => load(path.value));
load(path.value);

function onKey(e: KeyboardEvent) {
  if (e.key === "Escape") emit("close");
}
</script>

<template>
  <div class="backdrop" @click.self="emit('close')" @keydown="onKey" tabindex="-1">
    <div class="box" role="dialog" aria-label="Wybór katalogu">
      <h2>Wybierz katalog do skanowania</h2>

      <div class="top">
        <input
          v-model="path"
          class="mono"
          spellcheck="false"
          aria-label="Ścieżka"
          @keydown.enter.prevent="load(path)"
        >
        <button type="button" class="sec" :disabled="busy" @click="load(path)">Idź</button>
      </div>

      <div class="list">
        <p v-if="busy" class="empty">ładowanie…</p>
        <p v-else-if="err" class="err">{{ err }}</p>
        <template v-else-if="data">
          <p v-if="data.looksLikeWebRoot" class="note">
            <Icon name="check" /> Ten katalog wygląda na stronę ({{ data.markers.join(", ") }}) — prawdopodobnie to jest cel.
          </p>
          <div v-if="data.parent !== null" class="rowItem" @click="load(data.parent!)">
            <span><Icon name="chevron-up" /> ..</span>
          </div>
          <p v-if="!data.entries.length" class="empty">Brak podkatalogów.</p>
          <div v-for="e in data.entries" :key="e.path" class="rowItem" @click="load(e.path)">
            <span><Icon name="folder" /> {{ e.name }}</span>
            <span v-if="e.looksLikeWebRoot" class="badgeWeb">strona?</span>
          </div>
        </template>
      </div>

      <div class="row foot">
        <button type="button" @click="emit('pick', path)">Wybierz ten katalog</button>
        <button type="button" class="sec" @click="emit('close')">Anuluj</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.backdrop {
  position: fixed; inset: 0; background: rgba(5,8,14,.7); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: var(--s4); z-index: 50;
}
.box {
  background: var(--panel); border: 1px solid var(--bd2); border-radius: var(--r3);
  width: min(580px, 100%); max-height: 82vh; display: flex; flex-direction: column;
  box-shadow: 0 24px 64px -12px rgba(0,0,0,.6); overflow: hidden;
}
h2 {
  padding: var(--s3) var(--s4); border-bottom: 1px solid var(--bd);
  font-size: 13px; font-weight: 650; background: var(--panel2);
}
.top { display: flex; gap: var(--s2); padding: var(--s3) var(--s4); border-bottom: 1px solid var(--bd); }
.top input { flex: 1; font-size: 12px; }
.list { overflow-y: auto; flex: 1; padding: var(--s2); min-height: 140px; }
.rowItem {
  padding: 8px 10px; border-radius: var(--r1); cursor: pointer; font-size: 13px;
  display: flex; align-items: center; gap: var(--s2); border: 1px solid transparent;
}
.rowItem:hover { background: var(--panel3); border-color: var(--bd2); }
.badgeWeb {
  font-size: 10px; color: var(--ok); background: var(--ok-dim); border-radius: 99px;
  padding: 2px 8px; font-weight: 600; margin-left: auto;
}
.note {
  font-size: 12px; color: var(--ok); background: var(--ok-dim);
  padding: var(--s2) var(--s3); border-radius: var(--r1); margin: 0 0 var(--s2);
}
.foot { padding: var(--s3) var(--s4); border-top: 1px solid var(--bd); }
</style>
